import SecureLS from 'secure-ls';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  Messaging,
  onMessage,
} from 'firebase/messaging';
import app, { firebaseConfig } from '_technical/firebase/config';
import BaseService from 'services/api/BaseService';
import { UserUrls } from 'services/api/urls';
import { t } from 'i18next';

const ls = new SecureLS({ encodingType: 'aes' });
const FCM_TOKEN_KEY = 'fcm-token';
const NOTIFICATION_OPT_IN_DISMISSED_KEY = 'notifications-opt-in-dismissed';
const MESSAGING_SW_PATH = '/firebase-messaging-sw.js';

let tokenRefreshListenerAttached = false;
let tokenRequestInFlight: Promise<TokenRequestResult> | null = null;
let foregroundMessageListenerAttached = false;

type TokenRequestResult =
  | { status: 'success'; token: string }
  | {
      status:
        | 'unsupported'
        | 'permission-denied'
        | 'permission-default'
        | 'no-token'
        | 'error'
        | 'browser-blocked';
      shouldShowSettingsAlert?: boolean;
    };

export const getStoredFcmToken = (): string | undefined => {
  try {
    return ls.get(FCM_TOKEN_KEY) ? ls.get(FCM_TOKEN_KEY) : undefined;
  } catch (error) {
    return undefined;
  }
};

const setStoredFcmToken = (token: string) => {
  ls.set(FCM_TOKEN_KEY, token);
};

const clearStoredFcmToken = () => {
  ls.remove(FCM_TOKEN_KEY);
};

const setNotificationOptInDismissed = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(NOTIFICATION_OPT_IN_DISMISSED_KEY, 'true');
  } catch (error) {
    console.warn('Unable to persist notification opt-in state', error);
  }
};

const clearNotificationOptInDismissed = () => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(NOTIFICATION_OPT_IN_DISMISSED_KEY);
  } catch (error) {
    console.warn('Unable to clear notification opt-in state', error);
  }
};

const isNotificationOptInDismissed = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return localStorage.getItem(NOTIFICATION_OPT_IN_DISMISSED_KEY) === 'true';
  } catch (error) {
    console.warn('Unable to read notification opt-in state', error);
    return false;
  }
};

const isMessagingServiceWorker = (
  registration: ServiceWorkerRegistration,
): boolean => {
  const worker =
    registration.active || registration.waiting || registration.installing;
  return Boolean(worker?.scriptURL?.includes(MESSAGING_SW_PATH));
};

const getExistingMessagingRegistration = async () => {
  if (!('serviceWorker' in navigator)) {
    return undefined;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  return registrations.find(isMessagingServiceWorker);
};

const waitForServiceWorkerActivation = async (
  registration: ServiceWorkerRegistration,
): Promise<ServiceWorkerRegistration> => {
  if (registration.active) {
    return registration;
  }
  const worker = registration.installing || registration.waiting;
  if (!worker || typeof window === 'undefined') {
    return registration;
  }

  await new Promise<void>((resolve) => {
    const timeoutId = window.setTimeout(resolve, 5000);
    const onStateChange = () => {
      if (registration.active) {
        worker.removeEventListener('statechange', onStateChange);
        window.clearTimeout(timeoutId);
        resolve();
      }
    };
    worker.addEventListener('statechange', onStateChange);
  });

  return registration;
};

const registerMessagingServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    return undefined;
  }

  let registration =
    (await getExistingMessagingRegistration()) ??
    (await navigator.serviceWorker.register(MESSAGING_SW_PATH, {
      scope: '/',
    }));

  registration = await waitForServiceWorkerActivation(registration);

  try {
    await registration.update();
  } catch (error) {
    console.warn('Unable to update messaging service worker', error);
  }

  if (process.env.NODE_ENV !== 'production') {
    console.debug('[FCM] service worker', {
      scope: registration.scope,
      active: Boolean(registration.active),
      scriptURL:
        registration.active?.scriptURL ||
        registration.waiting?.scriptURL ||
        registration.installing?.scriptURL,
    });
  }

  try {
    const worker =
      registration.active || registration.waiting || registration.installing;
    worker?.postMessage({
      type: 'SET_FIREBASE_CONFIG',
      config: firebaseConfig,
    });
  } catch (error) {
    console.warn('Unable to send Firebase config to service worker', error);
  }

  return registration;
};

const getMessagingInstance = async (): Promise<Messaging | undefined> => {
  const supported = await isSupported().catch(() => false);
  if (!supported) {
    return undefined;
  }
  return getMessaging(app);
};

const isBraveBrowser = async (): Promise<boolean> => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const nav = navigator as unknown as {
    brave?: { isBrave?: () => Promise<boolean> };
  };
  if (nav.brave?.isBrave) {
    try {
      return await nav.brave.isBrave();
    } catch (error) {
      return false;
    }
  }
  return false;
};

const unsubscribeExistingPushSubscription = async (
  registration: ServiceWorkerRegistration,
) => {
  try {
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
    }
  } catch (error) {
    console.warn('Unable to unsubscribe existing push subscription', error);
  }
};

const getFcmToken = async (
  messaging: Messaging,
  registration: ServiceWorkerRegistration,
  vapidKey: string,
) => {
  try {
    return await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      await unsubscribeExistingPushSubscription(registration);
      try {
        await deleteToken(messaging);
      } catch (deleteError) {
        console.warn('Failed to delete Firebase token', deleteError);
      }
      return await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
    }
    throw error;
  }
};

const attachTokenRefreshListener = (messaging: Messaging) => {
  if (tokenRefreshListenerAttached) {
    return;
  }
  tokenRefreshListenerAttached = true;

  const maybeOnTokenRefresh = (
    messaging as unknown as {
      onTokenRefresh?: (callback: () => void) => void;
    }
  ).onTokenRefresh;

  if (typeof maybeOnTokenRefresh === 'function') {
    maybeOnTokenRefresh(() => {
      requestUserNotificationToken({ showSettingsAlert: false });
    });
  }
};

const attachForegroundMessageListener = (
  messaging: Messaging,
  defaultTitle: string,
) => {
  if (foregroundMessageListenerAttached) {
    return;
  }
  foregroundMessageListenerAttached = true;

  onMessage(messaging, (payload) => {
    if (
      typeof window === 'undefined' ||
      !('Notification' in window) ||
      Notification.permission !== 'granted' ||
      document.visibilityState !== 'visible'
    ) {
      return;
    }

    const notification = payload?.notification || {};
    const data = payload?.data || {};
    const title = notification.title || data.title || defaultTitle;
    const options = {
      body: notification.body || data.body,
      data,
      icon: 'https://espace.hellio-ingenierie.com/web-app-manifest-192x192.png',
      badge: 'https://espace.hellio-ingenierie.com/favicon-96x96.png',
      requireInteraction: true,
    };

    try {
      const instance = new Notification(title, options);
      const clickUrl =
        data?.clickUrl || data?.url || data?.link || window.location.origin;
      instance.onclick = () => {
        window.focus();
        if (clickUrl) {
          window.location.href = clickUrl;
        }
      };
    } catch (error) {
      console.warn('Failed to display foreground notification', error);
    }
  });
};

const syncDeviceToken = async (token: string) => {
  await BaseService.postRequest(
    UserUrls.ADD_DEVICE,
    {
      token,
    },
    true,
  );
  setStoredFcmToken(token);
  setNotificationOptInDismissed();
};

export const removeDeviceToken = async (token?: string) => {
  clearNotificationOptInDismissed();
  const storedToken = token || getStoredFcmToken();
  if (!storedToken) {
    return;
  }

  await BaseService.deleteRequest(
    UserUrls.REMOVE_DEVICE(storedToken),
    {},
    true,
  );

  const messaging = await getMessagingInstance();
  if (messaging) {
    try {
      await deleteToken(messaging);
    } catch (error) {
      console.warn('Failed to delete Firebase token', error);
    }
  }

  clearStoredFcmToken();
};

export const shouldShowNotificationOptIn = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (!('Notification' in window)) {
    return false;
  }
  const permission = Notification.permission;
  if (permission !== 'granted') {
    return true;
  }
  if (isNotificationOptInDismissed()) {
    return false;
  }
  return !getStoredFcmToken();
};

export const requestUserNotificationToken = async (
  {
    showSettingsAlert = false,
  }: {
    showSettingsAlert?: boolean;
  } = {
    showSettingsAlert: false,
  },
): Promise<TokenRequestResult> => {
  if (tokenRequestInFlight) {
    return tokenRequestInFlight;
  }

  tokenRequestInFlight = (async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return { status: 'unsupported' };
    }

    let permission = Notification.permission;
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }

    if (permission !== 'granted') {
      if (permission === 'denied') {
        await removeDeviceToken();
      }
      return {
        status:
          permission === 'denied' ? 'permission-denied' : 'permission-default',
        shouldShowSettingsAlert: showSettingsAlert,
      };
    }

    const messaging = await getMessagingInstance();
    if (!messaging) {
      return { status: 'unsupported' };
    }

    attachTokenRefreshListener(messaging);
    attachForegroundMessageListener(messaging, t('notifications:new'));

    try {
      const registration = await registerMessagingServiceWorker();
      if (!registration) {
        return { status: 'unsupported' };
      }
      const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        console.warn('Missing REACT_APP_FIREBASE_VAPID_KEY');
        return { status: 'error', shouldShowSettingsAlert: showSettingsAlert };
      }
      const token = await getFcmToken(messaging, registration, vapidKey);

      if (!token) {
        return {
          status: 'no-token',
          shouldShowSettingsAlert: showSettingsAlert,
        };
      }

      const storedToken = getStoredFcmToken();
      if (storedToken && storedToken === token) {
        setNotificationOptInDismissed();
        return { status: 'success', token };
      }
      if (storedToken && storedToken !== token) {
        await removeDeviceToken(storedToken);
      }

      await syncDeviceToken(token);

      return { status: 'success', token };
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'AbortError' &&
        getStoredFcmToken()
      ) {
        return { status: 'success', token: getStoredFcmToken() as string };
      }
      if (error instanceof DOMException && error.name === 'AbortError') {
        const isBrave = await isBraveBrowser();
        if (isBrave) {
          return { status: 'browser-blocked', shouldShowSettingsAlert: true };
        }
      }
      console.warn('Failed to request FCM token', error);
      return { status: 'error', shouldShowSettingsAlert: showSettingsAlert };
    }
  })();

  try {
    return await tokenRequestInFlight;
  } finally {
    tokenRequestInFlight = null;
  }
};
